/**
 * StartStep — seller registration entry point.
 *
 * One field: email. On submit → POST /sellers/register/start →
 * save `applicationId` to localStorage → call `onStarted(id, email)`.
 *
 * Resume path: if the user has a saved `applicationId` in localStorage
 * they can click the "continue previous" link — the WizardFlow handles
 * the GET /sellers/register/:id resume and hydration.
 *
 * RHF + Zod. shadcn Input + Label. i18n `seller-registration` namespace.
 * ≥44px tap target. aria-invalid on error. color is never the sole signal.
 */
import { type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { SELLER_REG_NS } from "@/features/seller-registration/api";
import {
  startSchema,
  startDefaultValues,
  saveApplicationId,
  type StartFormValues,
} from "@/features/seller-registration/schemas";
import { ApiError } from "@/lib/api-client";

const STEP_H1_CLASS =
  "text-[27px] font-bold leading-tight tracking-[-0.03em] text-foreground";

export interface StartStepProps {
  /** Called with the new applicationId UUID and the email once the server returns 200. */
  onStarted: (applicationId: string, email: string) => void;
  /** API call injected as a prop for testability. */
  apiRegisterStart: (email: string) => Promise<{ application_id: string; expires_at: string }>;
  /** Optional — shown if there is a saved applicationId to offer a resume path. */
  savedApplicationId?: string | null;
  onResume?: () => void;
}

export function StartStep({
  onStarted,
  apiRegisterStart,
  savedApplicationId,
  onResume,
}: StartStepProps): ReactElement {
  const { t } = useTranslation(SELLER_REG_NS);

  const form = useForm<StartFormValues>({
    resolver: zodResolver(startSchema),
    defaultValues: startDefaultValues,
    mode: "onBlur",
  });

  const emailError = form.formState.errors.email?.message;
  const rootError = form.formState.errors.root?.message;
  const isSubmitting = form.formState.isSubmitting;

  const onSubmit = async (values: StartFormValues): Promise<void> => {
    form.clearErrors("root");
    try {
      const data = await apiRegisterStart(values.email);
      saveApplicationId(data.application_id);
      onStarted(data.application_id, values.email);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined;
      const key = code ? `errors.${String(code)}` : "errors.generic";
      form.setError("root", { message: key });
    }
  };

  return (
    <div className="flex flex-1 flex-col justify-center gap-8">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h1 className={STEP_H1_CLASS} data-testid="start-h1">
          {t("start.h1")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("start.lede")}</p>
      </div>

      {/* Form */}
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        noValidate
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="reg-email">{t("start.emailLabel")}</Label>
          <Input
            id="reg-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoFocus
            placeholder={t("start.emailPlaceholder")}
            aria-required="true"
            aria-invalid={emailError !== undefined || undefined}
            aria-describedby={emailError !== undefined ? "reg-email-error" : undefined}
            disabled={isSubmitting}
            data-testid="reg-email"
            {...form.register("email")}
          />
          {emailError !== undefined ? (
            <p
              id="reg-email-error"
              role="alert"
              className="text-xs text-destructive"
              data-testid="reg-email-error"
            >
              {t(emailError)}
            </p>
          ) : null}
        </div>

        {rootError !== undefined ? (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-md border border-destructive/40 bg-destructive/5 p-3"
            data-testid="reg-start-error"
          >
            <p className="text-sm text-destructive">{t(rootError)}</p>
          </div>
        ) : null}

        <button
          type="submit"
          disabled={isSubmitting}
          data-testid="reg-start-submit"
          className={[
            "flex h-[44px] w-full items-center justify-center gap-2",
            "rounded-[var(--g-radius)] bg-primary px-4 text-sm font-semibold",
            "text-[var(--g-on-primary)] shadow-[var(--g-shadow-sm)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "focus-visible:ring-offset-2 disabled:opacity-60",
          ].join(" ")}
        >
          {isSubmitting ? (
            <Loader2 size={16} aria-hidden="true" className="animate-spin" />
          ) : null}
          {isSubmitting ? t("cta.submitting") : t("cta.start")}
        </button>
      </form>

      {/* Resume path */}
      {savedApplicationId && onResume ? (
        <p className="text-center text-sm text-muted-foreground">
          {t("start.alreadyHaveId")}{" "}
          <button
            type="button"
            onClick={onResume}
            className="text-primary underline-offset-4 hover:underline"
            data-testid="reg-resume-link"
          >
            {t("start.alreadyHaveIdLink")}
          </button>
        </p>
      ) : null}
    </div>
  );
}
